/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';

import { getRemainingCredits } from 'bundles/degree-home/components/v3-dpm/lib/graduationUtils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/degree-home';

import Legend, { progressBarColorMap } from './Legend';

type Props = {
  credits: number;
  completed?: number;
  inProgress?: number;
  planned?: number;
  isDegreeComplete?: boolean;
  footer?: React.ReactElement;
};

export type ProgressBarPropertyNameType = 'completed' | 'inProgress' | 'remaining' | 'planned';

export type ProgressBarPropertiesType = {
  credit: number;
  name: ProgressBarPropertyNameType;
  percentage?: number;
};

const styles = {
  progressBar: css`
    display: flex;
    align-items: flex-start;
    gap: var(--cds-spacing-50);
    align-self: stretch;
    box-sizing: unset;
    padding: var(--cds-spacing-50);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 6px;
    height: 20px;
    margin-top: var(--cds-spacing-400);

    div:first-of-type {
      border-top-left-radius: var(--cds-spacing-50);
      border-bottom-left-radius: var(--cds-spacing-50);
    }

    div:last-of-type {
      border-top-right-radius: var(--cds-spacing-50);
      border-bottom-right-radius: var(--cds-spacing-50);
    }

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,

  barBorderRadius: (borderRadius: string) => css`
    border-radius: ${borderRadius};
  `,

  barWidth: (option: ProgressBarPropertiesType) => css`
    width: ${option.percentage}%;
    height: 100%;
    background: ${progressBarColorMap[option.name]};
  `,

  legend: css`
    margin: var(--cds-spacing-300) 0 var(--cds-spacing-200);
    display: flex;
    justify-content: space-between;
    flex-direction: row;

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
  footer: css`
    div {
      justify-content: start;
      margin-top: 0;
    }
    ${breakpoints.down('md')} {
      display: none;
    }
  `,
};

const calculatePercentage = (partialCredits: number, credits: number) => {
  return (partialCredits / credits) * 100;
};

const ProgressBar = (props: Props) => {
  const { credits, completed = 0, inProgress = 0, planned = 0, isDegreeComplete = false, footer } = props;
  const remaining = getRemainingCredits(credits, completed, inProgress, planned);

  // do not show negative remaining credits
  const remainingCreditValue = remaining > 0 ? remaining : 0;

  // if the learner has more completed and in progress credits than the required credits, and the degree is still incomplete
  // use the summation as the base to calculate the percentage for completed and in progress credits
  const percentageBase = Math.max(completed + inProgress, credits);

  const progressBarProperties: ProgressBarPropertiesType[] = [
    { credit: completed, name: 'completed', percentage: calculatePercentage(completed, percentageBase) },
    {
      credit: inProgress,
      name: 'inProgress',
      percentage: calculatePercentage(inProgress, percentageBase),
    },
    { credit: planned, name: 'planned', percentage: calculatePercentage(planned, percentageBase) },
    {
      credit: remainingCreditValue,
      name: 'remaining',
      percentage: calculatePercentage(remaining, credits),
    },
  ];

  const visibleBars = progressBarProperties.filter((bar) => bar.credit !== 0);

  return (
    <React.Fragment>
      <TrackedDiv
        aria-label={_t('Degree progress bar')}
        css={styles.progressBar}
        trackingName="degree_progress_card"
        data={{ creditInfo: { completed, inProgress, planned, remaining: remainingCreditValue } }}
        withVisibilityTracking
      >
        {visibleBars.map((bar) => {
          const progressBarAriaLabel = (): string => {
            switch (bar.name) {
              case 'completed':
                return _t('Credits completed');
              case 'inProgress':
                return _t('Credits in progress');
              case 'planned':
                return _t('Credits added to plan');
              case 'remaining':
                return _t('Credits remaining');

              default:
                return '';
            }
          };

          return (
            <div
              role="progressbar"
              aria-valuenow={bar.percentage}
              arial-valuemin="0"
              aria-valuemax={100}
              aria-label={progressBarAriaLabel()}
              key={bar.name}
              css={styles.barWidth(bar)}
            />
          );
        })}
      </TrackedDiv>

      <div css={styles.legend}>
        <Legend legendItems={visibleBars} isDegreeComplete={isDegreeComplete} />
        {!!footer && <div css={styles.footer}>{footer}</div>}
      </div>
    </React.Fragment>
  );
};

export default ProgressBar;
